import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { SubSink } from 'subsink';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { fromEvent, of } from 'rxjs';
import { debounceTime, map, switchAll, tap } from 'rxjs/operators';
import { SmallDialogComponent } from '../../../../atoms/small-dialog/small-dialog.component';
import { stringsList } from '../../../../constants/app-config';
import { TranslateService } from '@ngx-translate/core';
import { AccountSchemaDataService, AccountSchemaModel } from '../../services/account-schema-data.service';
import { AccountSchemaAddDialogComponent } from '../account-schema-add-dialog/account-schema-add-dialog.component';
import { AccountSchemaBulkImportComponent } from '../account-schema-bulk-import/account-schema-bulk-import.component';

@Component({
  selector: 'app-account-schema-table',
  templateUrl: './account-schema-table.component.html',
  styleUrls: ['./account-schema-table.component.css'],
})
export class AccountSchemaTableComponent implements OnInit, AfterViewInit, OnDestroy {
  displayedColumns: string[] = ['checkbox', 'schema', 'nazivSheme', 'actions'];
  selectedInvoicesNum: number = 0;
  private subs = new SubSink();
  dataSource!: MatTableDataSource<AccountSchemaModel>;
  searchInput: string = '';
  isLoading = false;
  isSearchFilterOn = false;
  stringsList = stringsList;
  isHovered = false;
  isHovered1 = false;
  isAdditionalDataBeingFetched = false;
  userID: string | null = null;
  private savedScrollPosition = 0;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('scrollableContainer') scrollableContainer!: ElementRef<HTMLElement>;

  constructor(
    public schemaDataService: AccountSchemaDataService,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    public translate: TranslateService,
    private input: ElementRef<HTMLInputElement>
  ) {
    const obs = fromEvent(this.input.nativeElement, 'keyup').pipe(
      map((e: any) => e.target.value),
      debounceTime(250),
      tap(() => { this.isSearchFilterOn = true; }),
      map((searchInput: string) => of(searchInput?.toLowerCase())),
      switchAll()
    );
    obs.subscribe(async (data) => {
      if (data.length) {
        await this.schemaDataService.filterSchemas(this.userID as string, data).catch(console.error);
      } else {
        await this.schemaDataService.initializeData(this.userID as string).catch(console.error);
        this.isSearchFilterOn = false;
      }
    });
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.userID = localStorage.getItem('userID');
    this.schemaDataService.initializeData(this.userID as string).then(() => {
      this.subs.sink = this.schemaDataService.schemas.subscribe((data) => {
        this.dataSource = new MatTableDataSource<AccountSchemaModel>(data);
        this.dataSource.sort = this.sort;
        this.isLoading = false;
      });
    });
  }

  ngAfterViewInit(): void {
    if (this.dataSource) this.dataSource.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  async onScroll(e: any) {
    if (this.isSearchFilterOn) return;
    const container = this.scrollableContainer.nativeElement;
    const remaining = container.scrollHeight - (container.scrollTop + container.clientHeight);
    this.savedScrollPosition = container.scrollTop;
    if (remaining < container.clientHeight * 0.9 && !this.isAdditionalDataBeingFetched) {
      this.isAdditionalDataBeingFetched = true;
      await this.schemaDataService.fetchAdditional(this.userID as string).then(() => {
        this.isAdditionalDataBeingFetched = false;
      });
    }
  }

  addNewSchema(): void {
    const dialogRef = this.dialog.open(AccountSchemaAddDialogComponent, {
      width: '420px',
      data: { action: 'Add' },
    });
    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.snackBar.open('Schema successfully added', '', {
          duration: 5000,
          panelClass: ['white-snackbar'],
        });
      }
    });
  }

  openBulkImport(): void {
    this.dialog.open(AccountSchemaBulkImportComponent, {
      width: '480px',
      height: '386px',
      hasBackdrop: true,
    });
  }

  editSchema(schema: AccountSchemaModel): void {
    this.dialog.open(AccountSchemaAddDialogComponent, {
      width: '420px',
      data: { action: 'Edit', data: { ...schema } },
    });
  }

  toggleCheckbox(schema: AccountSchemaModel) {
    if (schema.selected) {
      this.selectedInvoicesNum++;
    } else {
      this.selectedInvoicesNum--;
    }
  }

  toggleAllSelection(seed: boolean) {
    if (seed) {
      this.dataSource.data.forEach((obj) => { obj.selected = true; });
      this.selectedInvoicesNum = this.dataSource.data.length;
    } else {
      this.dataSource.data.forEach((obj) => { obj.selected = false; });
      this.selectedInvoicesNum = 0;
    }
  }

  async bulkDeleteSchemas() {
    const schemasForDelete = this.dataSource.data.filter((s: any) => s.selected);
    const plural = schemasForDelete.length > 1;
    const messages = plural
      ? {
          heading: 'Delete schemas?',
          contentPartOne: 'Are you sure you want to delete these schemas?',
          action: 'Delete schemas',
        }
      : {
          heading: 'Delete schema?',
          contentPartOne: 'Are you sure you want to delete this schema?',
          action: 'Delete schema',
        };

    const dialogRef = this.dialog.open(SmallDialogComponent, {
      width: '460px',
      data: {
        heading: messages.heading,
        contentPartOne: messages.contentPartOne,
        action: messages.action,
      },
    });

    dialogRef.afterClosed().subscribe(async (response) => {
      if (response?.action === stringsList.doAction) {
        this.isLoading = true;
        const selected = this.dataSource.data.filter((s: any) => s.selected);
        await Promise.all(
          selected.map((s: any) =>
            this.schemaDataService.deleteAccountSchema(s.id!).catch((err) => console.log(err))
          )
        );
        this.dataSource.data = this.dataSource.data.filter((s: any) => !s.selected);
        (this.schemaDataService as any).dataStore.schemas = this.dataSource.data;
        this.selectedInvoicesNum = 0;
        this.snackBar.open(
          plural ? 'Selected schemas have been deleted' : 'Selected schema has been deleted',
          'Dismiss',
          { duration: 5000, panelClass: ['white-snackbar'] }
        );
        this.isLoading = false;
      }
    });
  }

  deleteSchema(item: AccountSchemaModel): void {
    const dialogRef = this.dialog.open(SmallDialogComponent, {
      width: '460px',
      data: {
        heading: 'Delete schema?',
        contentPartOne: 'Are you sure you want to delete schema: ',
        contentFile: item.schema,
        contentPartTwo: "You can't undo this action.",
        action: 'Delete schema',
      },
    });
    dialogRef.afterClosed().subscribe((response) => {
      if (response?.action === stringsList.doAction) {
        this.schemaDataService.removeSchema(item);
      }
    });
  }
}
